import React, { useContext, type ReactNode } from 'react'
import { Outlet, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { toast } from 'react-toastify'
import { useCookie } from 'react-use'
import { DateTime } from 'luxon'

import logo from '../../assets/proptrakx.png'
import { Loader, QueryContext, RootContext } from 'platform-core/ui'
import { RootService } from '@/services'
import { errorEye } from '@/util'
import {
  APP_DOMAIN,
  AUTHENTICATION_ERRORS,
  COOKIE_LENGTH,
  COOKIE_NAME,
  COOKIE_SAME_SITE_MODE
} from '@/util/constants'

export default React.memo(LayoutSignUpBase)

function LayoutSignUpBase (): ReactNode {
  const rootService = new RootService()
  const navigate = useNavigate()
  const [, updateCookie] = useCookie(COOKIE_NAME)

  const {
    setSessionToken,
    setAuthenticated,
    setSignUpEnabled
  } = useContext(RootContext)

  const { isLoading } = useQuery<boolean | null, Error>(['signUpEnabled'], async () => {
    const response = await rootService.isSignUpEnabled()
      .catch(err => {
        if (err.message.includes('maintenance mode')) {
          navigate('/error/#maintenance')
          return null
        }
        // Detect Auth errors, and kill the session
        if (AUTHENTICATION_ERRORS.some(str => err.message.includes(str))) {
          updateCookie('', {
            expires: DateTime.now().minus(COOKIE_LENGTH).toJSDate(),
            sameSite: COOKIE_SAME_SITE_MODE,
            domain: window.location.hostname || APP_DOMAIN
          })
          setAuthenticated(false)
          setSessionToken(null)
          navigate('/auth/login')
        } else {
          // Otherwise, just error out as normal
          errorEye(err)
          toast(
            'An error occurred loading the Dashboard. Please try again shortly.',
            { type: 'error' }
          )
          navigate('/error/#api')
        }
        return null
      })
    setSignUpEnabled(response ?? true)
    return response
  }, {
    refetchOnWindowFocus: false,
    context: QueryContext
  })

  return (
    <>
      {isLoading && <div className="flex h-screen items-center justify-center">
        <Loader imageClassName='w-24' />
      </div>}
      {!isLoading && <>
        <div className="bg-gray-50 dark:bg-gray-900 flex flex-col items-center justify-center px-6 py-8 mx-auto md:h-screen lg:py-0">
          <span className="flex items-center mb-6 text-2xl font-semibold text-gray-900 dark:text-white">
            <img className="h-14" src={logo} />
          </span>
          <div className="w-full bg-white rounded-lg shadow dark:border md:mt-0 sm:max-w-xl xl:p-0 dark:bg-gray-800 dark:border-gray-700">
            <div className="">
              <Outlet />
            </div>
          </div>
        </div>
      </>}
    </>
  )
}
